import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import {
  useSpeedGainLossTimelineSegments,
  SpeedCategory,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useRef, useEffect, memo } from "react";
import { StyledForeignObject } from "./BelowTimelineSegment";
import { getTimelineOffsetY } from "./skuOperatorTimelineUtils";
import { safariOffset } from "./safariOffset";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import { useSelector } from "store/useSelector";
import { speedGain, speedLoss } from "reports/constants/colors";
import { producingColor } from "constants/colors";

export function SpeedGainLossSegments({
  processId,
  order,
  width,
}: {
  processId: string;
  order: number;
  width: number;
}) {
  const [isTimelineExpanded] = useIsTimelineExpanded();

  return (
    <g
      className="speed-gain-loss-segments"
      style={{
        opacity: isTimelineExpanded ? 1 : 0,
        transition: `opacity 0.5s ease-in-out 1s`,
      }}
    >
      {isTimelineExpanded ? (
        <SpeedGainLossCanvasSegments
          processId={processId}
          order={order}
          width={width}
        />
      ) : null}
    </g>
  );
}

// Define colors for different speed categories
const SPEED_COLORS = {
  [SpeedCategory.ABOVE]: speedGain, // Green for above target speed
  [SpeedCategory.AT]: producingColor, // Light green for at target speed
  [SpeedCategory.BELOW]: speedLoss, // Red for below target speed
};

export const SpeedGainLossCanvasSegments = memo(
  ({
    processId,
    order,
    width,
  }: {
    processId: string;
    order: number;
    width: number;
  }) => {
    const [isTimelineExpanded] = useIsTimelineExpanded();
    const timelineOffsetY = getTimelineOffsetY(order);
    const { xLeft, xRight, scaleTime } = useTimeAxisContext();
    const visibleRange = useSelector(s => s.legacy.visibleRange);
    const x0 = visibleRange?.ge ? scaleTime(visibleRange.ge) || 0 : xLeft;
    const x1 = visibleRange?.lt
      ? scaleTime(visibleRange.lt) || 0
      : Math.min(scaleTime(Date.now()), xRight);
    const rectLeft = Math.max(x0, xLeft);
    const rectRight = Math.min(x1, xRight);
    const rectWidth = Math.max(1, rectRight - rectLeft);

    return (
      <>
        <StyledForeignObject
          $isHighlighted={false}
          x={rectLeft}
          y={timelineOffsetY + safariOffset}
          height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
          width={rectWidth}
          data-server-names={[]}
          $customCss={`z-index: 1000;`}
        >
          <SpeedGainLossCanvas
            width={width}
            processId={processId}
            isTimelineExpanded={isTimelineExpanded}
          />
        </StyledForeignObject>
      </>
    );
  }
);

interface SpeedGainLossCanvasProps {
  width: number;
  processId: string;
  isTimelineExpanded: boolean;
}

const SpeedGainLossCanvas: React.FC<SpeedGainLossCanvasProps> = ({
  width,
  processId,
  isTimelineExpanded,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { scaleTime } = useTimeAxisContext();
  const speedGainLossSegments = useSpeedGainLossTimelineSegments();

  // paint the segments onto the canvas
  useEffect(() => {
    // Get all segments for this process
    const segments = speedGainLossSegments[processId] || [];

    if (!canvasRef.current) {
      console.warn("Canvas ref not available");
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.("2d");
    if (!ctx) {
      console.warn("Canvas context not available");
      return;
    }

    // Only render when timeline is expanded
    if (isTimelineExpanded) {
      // Clear canvas and draw background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#f5f5f5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // If no segments, log warning
      if (!segments || segments.length === 0) {
        console.log(`No segments to draw for process ${processId}`);
        return;
      }

      // Draw each segment
      segments.forEach(segment => {
        if (
          !segment.range ||
          typeof segment.range.ge !== "number" ||
          typeof segment.range.lt !== "number"
        ) {
          console.warn("Invalid segment range:", segment.range);
          return;
        }

        // Get pixel coordinates for segment boundaries
        const x1 = scaleTime(segment.range.ge);
        const x2 = scaleTime(segment.range.lt);

        if (isNaN(x1) || isNaN(x2)) {
          console.warn(
            `Invalid segment coordinates after scaling: x1=${x1}, x2=${x2}`
          );
          return;
        }

        // Ensure minimum width and actually visible on screen
        const width = Math.max(1, x2 - x1);

        // Skip if off-screen or has zero/negative width
        if (x1 >= canvas.width || x2 <= 0 || width <= 0) {
          return;
        }

        // Draw the segment
        ctx.fillStyle = SPEED_COLORS[segment.category] ?? "#cccccc";
        ctx.fillRect(x1, 0, width, TAGHIGHLIGHT_BACKDROP_HEIGHT);

        // If segment is wide enough, add text
        if (width > 30) {
          const text = `${Math.round(segment.speedPercentage)}%`;
          ctx.fillStyle =
            segment.category === SpeedCategory.BELOW ? "#fff" : "#000";
          ctx.font = "12px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(
            text,
            x1 + width / 2,
            TAGHIGHLIGHT_BACKDROP_HEIGHT / 2 + 1
          );
        }
      });
    } else {
      // Just clear canvas when timeline is collapsed
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isTimelineExpanded, speedGainLossSegments, scaleTime, processId, width]);

  return (
    <canvas
      ref={canvasRef}
      data-tick-type="speedGainLoss"
      id={`speed-gain-loss-canvas-${processId}`}
      className={`ticks-canvas speed-gain-loss-canvas`}
      width={`${width}px`}
      height={`${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`}
      style={{
        width: `${width}px`,
        height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
        cursor: "pointer",
        opacity: isTimelineExpanded ? 1 : 0,
        transition: `opacity 0.5s ease-in-out 1s`,
        pointerEvents: isTimelineExpanded ? "auto" : "none",
      }}
    />
  );
};
